import { useTranslation } from 'react-i18next';
import { List, ListItemIcon, ListItemText, ListItemButton, useTheme, useMediaQuery, Collapse } from '@mui/material';
import { ChevronRight, ExpandMore, FiberManualRecord } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { listMenuStyles } from 'src/shared/components/navigations/side-menu/sideMenu.styles';
import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { SideMenuDrawer } from './sideMenuDrawer';
import { SideMenuLink } from './sideMenuLink';
import { SideMenuLoading } from './sideMenuLoading';
import { SideMenuTooltip } from './sideMenuTooltip';
import { Colors } from '../../styles/colors';
import { useSideMenuMember } from './useSideMenuMember';

const SideMenuMember = () => {
  const { sideMenuOpen, setSideMenuOpen, currentMember } = useGlobals();
  const { t, ready } = useTranslation(['common']);

  const location = useLocation();
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));

  const isMenuItemDisabled = false;

  const sideMenuMemberItems = useSideMenuMember();

  const locationMatchesArray = (array: Array<string>) => {
    let result = false;
    array.forEach((x) => {
      const match = matchPath({ path: `${x}/*` }, location.pathname);
      if (match) result = true;
    });
    return result;
  };

  const closeMenuOnMobileDevices = useCallback(
    (isParent, hasChildren) => {
      const closeOnClick = (isParent && !hasChildren) || !isParent;
      if (isMobileDevice && closeOnClick) {
        setSideMenuOpen(false);
      }
    },
    [isMobileDevice, setSideMenuOpen],
  );

  return (
    <SideMenuDrawer>
      <List component='nav' id='sidemenu' sx={listMenuStyles}>
        {ready && currentMember ? (
          <>
            {sideMenuMemberItems
              .filter((item) => item.showMenuItem)
              .map((item) => {
                const isActive = item.subMenuItems
                  ? locationMatchesArray([item.link.toString()].concat(item.subMenuItems.map((a) => a.link.toString())))
                  : false;
                return (
                  <SideMenuTooltip
                    showTooltip={isMenuItemDisabled || !sideMenuOpen}
                    key={item.key}
                    label={item.label}
                    isMenuItemDisabled={isMenuItemDisabled}
                  >
                    <ListItemButton
                      key={item.key}
                      component={SideMenuLink as any}
                      onClick={() => closeMenuOnMobileDevices(true, !!item.subMenuItems)}
                      to={item.link}
                      disabled={isMenuItemDisabled}
                      aria-hidden={isMenuItemDisabled}
                      aria-label={t(item.label)}
                      sx={{ borderRadius: 1 }}
                      className={item.subMenuItems && isActive ? 'active' : undefined}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: Colors.LinkColor }} aria-hidden>
                        {item.Icon}
                      </ListItemIcon>
                      <ListItemText primary={t(item.label)} sx={{ ml: sideMenuOpen ? 0 : 1 }} />
                      {item.subMenuItems && <>{isActive ? <ExpandMore /> : <ChevronRight />}</>}
                    </ListItemButton>
                    {item.subMenuItems && (
                      <Collapse in={isActive} timeout='auto' unmountOnExit>
                        <List
                          component='div'
                          disablePadding
                          dense
                          sx={{
                            mt: 0.5,
                            mb: 1,
                            '& .subMenuText': {
                              fontWeight: 300,
                            },
                            '& .dotIndicator': {
                              opacity: 0.6,
                            },
                            '& .subMenuItem.active': {
                              '& .subMenuText': {
                                fontWeight: 600,
                                opacity: 1,
                              },
                              '& .dotIndicator': {
                                opacity: 1,
                              },
                            },
                          }}
                        >
                          {item.subMenuItems
                            // .filter((x) => getPermission(currentUser, x.permission ?? '')) // Filtrerar bort de man inte har behörighet att se
                            .filter((x) => x.showMenuItem)
                            .map((subItem) => {
                              const isSubMenuItemDisabled = false;
                              return (
                                <SideMenuTooltip
                                  showTooltip={isSubMenuItemDisabled}
                                  key={subItem.key}
                                  label={subItem.label}
                                  isMenuItemDisabled={isSubMenuItemDisabled}
                                >
                                  <ListItemButton
                                    key={subItem.key}
                                    disabled={isSubMenuItemDisabled}
                                    to={subItem.link}
                                    onClick={() => closeMenuOnMobileDevices(false, false)}
                                    className='subMenuItem'
                                    component={SideMenuLink as any}
                                  >
                                    <ListItemIcon sx={{ minWidth: sideMenuOpen ? 35 : 15 }} aria-hidden className='dotIndicator'>
                                      <FiberManualRecord sx={{ fontSize: '0.6rem' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={sideMenuOpen ? t(subItem.label) : t(subItem.label).substring(0, 1)}
                                      className='subMenuText'
                                    />
                                  </ListItemButton>
                                </SideMenuTooltip>
                              );
                            })}
                        </List>
                      </Collapse>
                    )}
                  </SideMenuTooltip>
                );
              })}
          </>
        ) : (
          <SideMenuLoading />
        )}
      </List>
    </SideMenuDrawer>
  );
};

export default SideMenuMember;
